import React, { type ComponentType, useCallback, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import type { PreloadedHydrateJqlBuilderQuery } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/useHydrationQueryLoader.tsx';
import {
	ContextualAnalyticsData,
	SCREEN,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import type { JQLBuilderUI$data } from '@atlassian/jira-jql-builder/src/ui/__generated__/JQLBuilderUI.graphql';
import type { AdvancedContent$data } from '@atlassian/jira-filter-refinement/src/ui/filter-popup/filter-popup-content/ui/advanced/__generated__/AdvancedContent.graphql';
import type { AddFilterButtonComponent, NoOptionComponentType } from '../common/types.tsx';
import type {
	JQLEditorUIOldProps,
	JQLEditorUIProps,
	JiraProjectOptionsType,
} from '../common/ui/types.tsx';
import { ASTContainer } from '../controllers/ast/index.tsx';
import { SearchStateContainer } from '../controllers/search-state/index.tsx';
import { FeatureContextProvider } from '../controllers/feature-context.tsx';
import JQLEditorUIFallback, {
	type JQLEditorUIFallbackProps,
} from './jql-editor-fallback/index.tsx';

export type JQLBuilderBasicUIOldProps = {
	query: string;
	JQLEditor: ComponentType<JQLEditorUIOldProps>;
	excludedFields?: string[];
	onSearch?: (jql: string) => void;
	onSwitchToJql?: () => void;
	onReady?: () => void;
	onLoad?: () => void;
	onJourneyStart?: () => Attributes;
	NoOptionComponent?: NoOptionComponentType;
	projectOptions?: JiraProjectOptionsType;
	singleSelectFields?: string[];
	hydrationQueryRef: PreloadedHydrateJqlBuilderQuery;
	isHydrating: boolean;
	shouldUseListMarkup?: boolean;
	hideOperatorDropdown?: boolean;
	irremovableFields: JQLEditorUIProps['irremovableFields'];
	hideTextSearchInput?: boolean;
	searchPlaceholder?: string;
	showOrderByPicker?: boolean;
	contextJql: string;
	viewContext?: JQLEditorUIProps['viewContext'];
	scope?: JQLEditorUIProps['scope'];
	suggestedUsersCount?: number;
	verticalMode?: boolean;
	shouldRenderToParent?: boolean;
};

export type JQLBuilderBasicUIProps = {
	query: string;
	JQLEditor: ComponentType<JQLEditorUIProps>;
	excludedFields?: string[];
	onSearch?: (jql: string) => void;
	onSwitchToJql?: () => void;
	onReady?: () => void;
	onLoad?: () => void;
	onJourneyStart?: () => Attributes;
	NoOptionComponent?: NoOptionComponentType;
	AddFilterButtonComponent?: AddFilterButtonComponent;
	projectOptions?: JiraProjectOptionsType;
	singleSelectFields?: string[];
	hydrationData: JQLBuilderUI$data | AdvancedContent$data;
	isHydrating: boolean;
	shouldUseListMarkup?: boolean;
	hideOperatorDropdown?: boolean;
	irremovableFields: JQLEditorUIProps['irremovableFields'];
	hideTextSearchInput?: boolean;
	searchPlaceholder?: string;
	showOrderByPicker?: boolean;
	contextJql: string;
	viewContext?: JQLEditorUIProps['viewContext'];
	scope?: JQLEditorUIProps['scope'];
	suggestedUsersCount?: number;
	verticalMode?: boolean;
	shouldRenderToParent?: boolean;
};

const debouncedSearch = debounce((onSearch, newJql) => onSearch(newJql), 1000, {
	leading: false,
	trailing: true,
});

const JQLBuilderBasicUIOld = (props: JQLBuilderBasicUIOldProps) => {
	const {
		query,
		JQLEditor,
		onSearch: triggerSearch,
		onSwitchToJql,
		excludedFields,
		onReady,
		onLoad,
		onJourneyStart,
		NoOptionComponent,
		projectOptions,
		singleSelectFields,
		hydrationQueryRef,
		isHydrating,
		shouldUseListMarkup,
		hideOperatorDropdown,
		irremovableFields,
		hideTextSearchInput,
		searchPlaceholder,
		showOrderByPicker,
		contextJql,
		viewContext = null,
		scope = null,
		suggestedUsersCount,
		verticalMode = false,
		shouldRenderToParent = true,
	} = props;

	const [jql, setJql] = useState(query);

	const onSearch = useCallback(
		(newJql: string) => {
			setJql(newJql);
			triggerSearch && debouncedSearch(triggerSearch, newJql);
		},
		[triggerSearch],
	);

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
		setJql(query);
	}, [query, setJql]);

	return (
		<ASTContainer jql={jql} onSwitchToJql={onSwitchToJql}>
			<SearchStateContainer excludedFields={excludedFields} irremovableFields={irremovableFields}>
				<FeatureContextProvider
					shouldRenderToParent={shouldRenderToParent}
					verticalMode={verticalMode}
				>
					<JQLEditor
						NoOptionComponent={NoOptionComponent}
						query={jql}
						onSearch={onSearch}
						onSwitchToJql={onSwitchToJql}
						excludedFields={excludedFields}
						onReady={onReady}
						onLoad={onLoad}
						onJourneyStart={onJourneyStart}
						hydrationQueryRef={hydrationQueryRef}
						isHydrating={isHydrating}
						projectOptions={projectOptions}
						singleSelectFields={singleSelectFields}
						shouldUseListMarkup={shouldUseListMarkup}
						hideOperatorDropdown={hideOperatorDropdown}
						irremovableFields={irremovableFields}
						hideTextSearchInput={hideTextSearchInput}
						searchPlaceholder={searchPlaceholder}
						showOrderByPicker={showOrderByPicker}
						contextJql={contextJql}
						viewContext={viewContext}
						scope={scope}
						suggestedUsersCount={suggestedUsersCount}
					/>
				</FeatureContextProvider>
			</SearchStateContainer>
		</ASTContainer>
	);
};

const JQLBuilderBasicUI = (props: JQLBuilderBasicUIProps) => {
	const {
		query,
		JQLEditor,
		onSearch: triggerSearch,
		onSwitchToJql,
		excludedFields,
		onReady,
		onLoad,
		onJourneyStart,
		NoOptionComponent,
		AddFilterButtonComponent,
		projectOptions,
		singleSelectFields,
		hydrationData,
		isHydrating,
		shouldUseListMarkup,
		hideOperatorDropdown,
		irremovableFields,
		hideTextSearchInput,
		searchPlaceholder,
		showOrderByPicker,
		contextJql,
		viewContext = null,
		scope = null,
		suggestedUsersCount,
		verticalMode = false,
		shouldRenderToParent = true,
	} = props;

	const [jql, setJql] = useState(query);

	const onSearch = useCallback(
		(newJql: string) => {
			setJql(newJql);
			triggerSearch && debouncedSearch(triggerSearch, newJql);
		},
		[triggerSearch],
	);

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
		setJql(query);
	}, [query, setJql]);

	return (
		<ASTContainer jql={jql} onSwitchToJql={onSwitchToJql}>
			<SearchStateContainer excludedFields={excludedFields} irremovableFields={irremovableFields}>
				<FeatureContextProvider
					shouldRenderToParent={shouldRenderToParent}
					verticalMode={verticalMode}
				>
					<JQLEditor
						NoOptionComponent={NoOptionComponent}
						AddFilterButtonComponent={AddFilterButtonComponent}
						query={jql}
						onSearch={onSearch}
						onSwitchToJql={onSwitchToJql}
						excludedFields={excludedFields}
						onReady={onReady}
						onLoad={onLoad}
						onJourneyStart={onJourneyStart}
						hydrationData={hydrationData}
						isHydrating={isHydrating}
						projectOptions={projectOptions}
						singleSelectFields={singleSelectFields}
						shouldUseListMarkup={shouldUseListMarkup}
						hideOperatorDropdown={hideOperatorDropdown}
						irremovableFields={irremovableFields}
						hideTextSearchInput={hideTextSearchInput}
						searchPlaceholder={searchPlaceholder}
						showOrderByPicker={showOrderByPicker}
						contextJql={contextJql}
						viewContext={viewContext}
						scope={scope}
						suggestedUsersCount={suggestedUsersCount}
					/>
				</FeatureContextProvider>
			</SearchStateContainer>
		</ASTContainer>
	);
};

export const JQLBuilderBasicFallback = ({
	query: jql,
	...props
}: JQLEditorUIFallbackProps & { query: string }): React.JSX.Element => (
	<ASTContainer jql={jql}>
		<SearchStateContainer
			excludedFields={props.excludedFields}
			irremovableFields={props.irremovableFields}
		>
			<JQLEditorUIFallback {...props} />
		</SearchStateContainer>
	</ASTContainer>
);

export const JQLBuilderBasicUIWithAnalyticsOld = (props: JQLBuilderBasicUIOldProps) => (
	<ContextualAnalyticsData sourceName="jqlBuilderBasic" sourceType={SCREEN}>
		<JQLBuilderBasicUIOld {...props} />
	</ContextualAnalyticsData>
);

const JQLBuilderBasicUIWithAnalytics = (props: JQLBuilderBasicUIProps): React.JSX.Element => (
	<ContextualAnalyticsData sourceName="jqlBuilderBasic" sourceType={SCREEN}>
		<JQLBuilderBasicUI {...props} />
	</ContextualAnalyticsData>
);

export default JQLBuilderBasicUIWithAnalytics;
